import React, { useState } from 'react';
import {
  Box,
  IconButton,
  ToggleButtonGroup,
  ToggleButton,
  TextField,
  InputAdornment,
  Badge,
  Typography,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  ArrowForward as ArrowForwardIcon,
  Search as SearchIcon,
  FilterList as FilterListIcon,
} from '@mui/icons-material';
import MobileStatCards from './MobileStatCards';
import MobileLeaderboard from './MobileLeaderboard';
import NewsSection from './NewsSection';

const MobileDashboard = ({
  stats,
  viewMode,
  topAgents,
  profilePictures,
  dateRange,
  periodLabel,
  isAdmin,
  onDateRangeChange,
  onDateShift,
  searchTerm,
  onSearchChange,
  activeFilters,
  onFilterClick,
  t,
}) => {
  const [searchOpen, setSearchOpen] = useState(false);

  return (
    <Box sx={{ p: 1 }}>
      {/* Period Label */}
      <Typography
        variant="h6"
        sx={{
          fontWeight: 700,
          mb: 1,
          color: 'text.primary',
        }}
      >
        {periodLabel}
      </Typography>

      {/* Search & Filter Row */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 1 }}>
        {searchOpen ? (
          <TextField
            size="small"
            variant="outlined"
            placeholder={viewMode === 'agency' ? t('dashboard.searchAgencies') : t('dashboard.searchWriters')}
            value={searchTerm}
            onChange={(e) => onSearchChange(e.target.value)}
            autoFocus
            onBlur={() => { if (!searchTerm) setSearchOpen(false); }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
            sx={{ flex: 1 }}
          />
        ) : (
          <IconButton
            size="small"
            onClick={() => setSearchOpen(true)}
            sx={{
              border: '1px solid',
              borderColor: 'divider',
              '&:hover': { borderColor: 'primary.main' },
            }}
          >
            <SearchIcon fontSize="small" />
          </IconButton>
        )}
        <IconButton
          size="small"
          onClick={onFilterClick}
          sx={{
            border: '1px solid',
            borderColor: 'divider',
            '&:hover': { borderColor: 'primary.main' },
          }}
        >
          <Badge badgeContent={activeFilters} color="primary">
            <FilterListIcon fontSize="small" />
          </Badge>
        </IconButton>
      </Box>

      {/* Date Range Controls */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 1.5 }}>
        <IconButton
          size="small"
          onClick={() => onDateShift('back')}
          disabled={dateRange === 'ytd'}
          sx={{
            border: '1px solid',
            borderColor: 'divider',
            '&:hover': { borderColor: 'primary.main' },
          }}
        >
          <ArrowBackIcon fontSize="small" />
        </IconButton>

        <ToggleButtonGroup
          value={dateRange}
          exclusive
          onChange={(e, value) => value && onDateRangeChange(value)}
          size="small"
          sx={{
            flex: 1,
            '& .MuiToggleButton-root': {
              flex: 1,
              border: '1px solid',
              borderColor: 'divider',
              fontSize: '0.75rem',
              '&.Mui-selected': {
                bgcolor: 'primary.main',
                color: 'primary.contrastText',
                '&:hover': { bgcolor: 'primary.dark' },
              },
            },
          }}
        >
          <ToggleButton value="day">{t('common.day')}</ToggleButton>
          <ToggleButton value="week">{t('common.week')}</ToggleButton>
          <ToggleButton value="month">{t('common.month')}</ToggleButton>
          <ToggleButton value="ytd">{t('common.ytd')}</ToggleButton>
        </ToggleButtonGroup>

        <IconButton
          size="small"
          onClick={() => onDateShift('forward')}
          disabled={dateRange === 'ytd'}
          sx={{
            border: '1px solid',
            borderColor: 'divider',
            '&:hover': { borderColor: 'primary.main' },
          }}
        >
          <ArrowForwardIcon fontSize="small" />
        </IconButton>
      </Box>

      <MobileStatCards stats={stats} viewMode={viewMode} t={t} />

      <MobileLeaderboard
        topAgents={topAgents}
        profilePictures={profilePictures}
        t={t}
      />

      <NewsSection isAdmin={isAdmin} t={t} />
    </Box>
  );
};

export default MobileDashboard;
